import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { SUPPRESS_NOT_FOUND_NOTIFICATION } from 'core';
import { Observable, catchError, finalize, forkJoin, map, of, switchMap, tap, throwError } from 'rxjs';
import {
  NewCustomerAddress,
  NewCustomerCompany,
  NewCustomerCompanyDetail,
  NewCustomerCompanyList,
  NewCustomerOption,
  NewCustomerTerritory,
} from '../interfaces/new-customer.model';
import {
  NewCustomerContactRequest,
  NewCustomerDelivery,
  NewCustomerNoteRequest,
  NewCustomerPayment,
  NewCustomerRegistry,
  NewCustomerResult,
  NewCustomerSearchFilter,
  NewCustomerSearchRequest,
} from '../interfaces/new-customer-requests.model';
import { OrderProposal } from '../interfaces/order-proposal.model';
import { matchesSearchFilter, searchType } from '../utils/new-customer.utils';
import { OrderProposalService } from './order-proposal.service';

export type NewCustomerStep =
  | 'search'
  | 'results'
  | 'detail'
  | 'manual'
  | 'deliveries'
  | 'deliveryForm'
  | 'zones'
  | 'contact'
  | 'bank'
  | 'note'
  | 'gdpr';

const EMPTY_FILTER: NewCustomerSearchFilter = { text: '', comune: null, provincia: null };

const EMPTY_PAYMENT: NewCustomerPayment = {
  condizione: null,
  contatto: null,
  abi: null,
  cab: null,
  banca: null,
  agenzia: null,
  codiceSdi: null,
  esenzioneIva: false,
};

// Flusso nuovo cliente della proposta: gli step si aprono in sequenza e i dati restano tornando indietro
@Injectable({ providedIn: 'root' })
export class NewCustomerService {
  private readonly http = inject(HttpClient);
  private readonly proposalService = inject(OrderProposalService);

  // Step aperti, dal primo all'attuale: «Indietro» chiude l'ultimo
  private readonly _steps = signal<NewCustomerStep[]>(['search']);
  readonly step = computed(() => this._steps()[this._steps().length - 1]);
  readonly canGoBack = computed(() => this._steps().length > 1);

  readonly idOrdine = signal('');

  // Ricerca e azienda scelta
  readonly searchRequest = signal<NewCustomerSearchRequest>({});
  readonly companies = signal<NewCustomerCompanyList | null>(null);
  readonly searchFilter = signal<NewCustomerSearchFilter>(EMPTY_FILTER);
  readonly company = signal<NewCustomerCompany | null>(null);
  readonly detail = signal<NewCustomerCompanyDetail | null>(null);
  readonly addresses = signal<NewCustomerAddress[]>([]);

  // Dati del nuovo cliente
  readonly registry = signal<NewCustomerRegistry | null>(null);
  readonly delivery = signal<NewCustomerDelivery | null>(null);
  readonly zones = signal<NewCustomerOption[]>([]);
  readonly zoneOil = signal<NewCustomerOption | null>(null);
  readonly zoneNonOil = signal<NewCustomerOption | null>(null);
  readonly territory = signal<NewCustomerTerritory | null>(null);
  readonly contact = signal<NewCustomerContactRequest | null>(null);
  readonly payments = signal<NewCustomerOption[]>([]);
  readonly payment = signal<NewCustomerPayment>(EMPTY_PAYMENT);
  readonly note = signal<NewCustomerNoteRequest | null>(null);
  readonly bankDocuments = signal<File[]>([]);
  readonly vatDocuments = signal<File[]>([]);
  readonly gdprDocuments = signal<File[]>([]);

  readonly loading = signal(false);
  readonly downloading = signal(false);
  readonly creating = signal(false);

  // Esito del flusso: la pagina lo legge e chiude
  readonly result = signal<NewCustomerResult | null>(null);

  readonly searchType = computed(() => searchType(this.searchRequest()));
  readonly filteredCompanies = computed(() =>
    (this.companies()?.items ?? []).filter(company => matchesSearchFilter(company, this.searchType(), this.searchFilter())));
  readonly hasExistingCompany = computed(() => (this.companies()?.items ?? []).some(company => !!company.idBusinessPartner));
  readonly hasOtherZoneCompany = computed(() => (this.companies()?.items ?? []).some(company => company.codificatoAltraZona));
  readonly oilZones = computed(() => this.zones().filter(zone => zone.type === 'Oil'));
  readonly nonOilZones = computed(() => this.zones().filter(zone => zone.type === 'NonOil'));
  // Con una sola zona per tipologia non c'è niente da scegliere
  readonly needsZoneChoice = computed(() => this.oilZones().length > 1 || this.nonOilZones().length > 1);
  readonly hasZone = computed(() => !!this.zoneOil() || !!this.zoneNonOil());
  readonly outOfZone = computed(() => {
    const territory = this.territory();
    return !!territory && [territory.esitoOil, territory.esitoNonOil]
      .some(esito => esito === 'FuoriZona' || esito === 'Escluso' || esito === 'NonVerificabile');
  });
  // Dati inseriti o ricevuti che chiudendo il flusso andrebbero persi
  readonly hasData = computed(() => this.companies() !== null || this.registry() !== null);
  // Società di capitali: niente consensi da raccogliere, solo l'informativa da consegnare.
  // Forma giuridica non censita o anagrafica scritta a mano: come un soggetto GDPR
  readonly consensiGdpr = computed(() => this.detail()?.soggettoGdpr ?? true);

  // Un flusso nuovo riparte dalla ricerca senza i dati del precedente
  start(idOrdine: string): void {
    this.idOrdine.set(idOrdine);
    this._steps.set(['search']);
    this.searchRequest.set({});
    this.companies.set(null);
    this.searchFilter.set(EMPTY_FILTER);
    this.company.set(null);
    this.detail.set(null);
    this.addresses.set([]);
    this.registry.set(null);
    this.delivery.set(null);
    this.zones.set([]);
    this.zoneOil.set(null);
    this.zoneNonOil.set(null);
    this.territory.set(null);
    this.contact.set(null);
    this.payments.set([]);
    this.payment.set(EMPTY_PAYMENT);
    this.note.set(null);
    this.bankDocuments.set([]);
    this.vatDocuments.set([]);
    this.gdprDocuments.set([]);
    this.loading.set(false);
    this.downloading.set(false);
    this.creating.set(false);
    this.result.set(null);
  }

  go(step: NewCustomerStep): void {
    this._steps.update(steps => [...steps, step]);
  }

  back(): void {
    this._steps.update(steps => (steps.length > 1 ? steps.slice(0, -1) : steps));
  }

  // Ricerca nel Registro delle Imprese: troppi risultati è un 400 col motivo, mostrato dall'interceptor
  search(request: NewCustomerSearchRequest): Observable<NewCustomerCompanyList> {
    const params: Record<string, string> = {};
    if (request.ragioneSociale) {
      params['ragioneSociale'] = request.ragioneSociale;
    }
    if (request.codiceFiscale) {
      params['codiceFiscale'] = request.codiceFiscale;
    }
    if (request.partitaIva) {
      params['partitaIva'] = request.partitaIva;
    }

    this.loading.set(true);
    return this.http.get<NewCustomerCompanyList>('api/ordini/nuovocliente/aziende', { params }).pipe(
      tap(companies => {
        this.searchRequest.set(request);
        this.companies.set(companies);
        this.searchFilter.set({ ...EMPTY_FILTER });
        this.company.set(null);
      }),
      finalize(() => this.loading.set(false)),
    );
  }

  applySearchFilter(filter: NewCustomerSearchFilter): void {
    this.searchFilter.set(filter);
  }

  selectCompany(company: NewCustomerCompany): void {
    this.company.set(company);
  }

  // Dettaglio e sedi dell'azienda: senza sedi resta il nuovo destino
  loadCompany(company: NewCustomerCompany): Observable<NewCustomerCompanyDetail> {
    const loaded = this.detail();
    if (loaded && loaded.companyId === company.companyId) {
      this.company.set(company);
      return of(loaded);
    }

    this.loading.set(true);
    return forkJoin({
      detail: this.http.get<NewCustomerCompanyDetail>(`api/ordini/nuovocliente/aziende/${company.companyId}`, {
        context: new HttpContext().set(SUPPRESS_NOT_FOUND_NOTIFICATION, true),
      }),
      addresses: this.http.get<NewCustomerAddress[]>(`api/ordini/nuovocliente/aziende/${company.companyId}/indirizzi`, {
        context: new HttpContext().set(SUPPRESS_NOT_FOUND_NOTIFICATION, true),
      }).pipe(catchError(() => of([] as NewCustomerAddress[]))),
    }).pipe(
      tap(({ detail, addresses }) => {
        this.company.set(company);
        this.detail.set(detail);
        this.registry.set(this.registryFromDetail(detail));
        this.addresses.set(addresses);
        this.delivery.set(null);
        this.territory.set(null);
      }),
      map(({ detail }) => detail),
      finalize(() => this.loading.set(false)),
    );
  }

  // Azienda scelta: se è già in anagrafica il flusso si chiude, altrimenti si passa al destino
  confirmCompany(company: NewCustomerCompany): Observable<unknown> {
    if (company.idBusinessPartner) {
      this.result.set({ idBusinessPartner: company.idBusinessPartner });
      return of(null);
    }

    this.company.set(company);
    return this.loadCompany(company).pipe(tap(() => this.go('deliveries')));
  }

  // Anagrafica inserita a mano: il destino della ricerca non vale più
  setRegistry(registry: NewCustomerRegistry): void {
    this.registry.set(registry);
    this.addresses.set([]);
    this.company.set(null);
    this.detail.set(null);
    if (this.delivery()?.branchId) {
      this.delivery.set(null);
    }
    this.territory.set(null);
  }

  setDelivery(delivery: NewCustomerDelivery | null): void {
    this.delivery.set(delivery);
  }

  // Zone di codifica dell'utente: con una sola zona per tipologia la tipologia è già scelta e lo step si salta,
  // tranne quando il cliente risulta fuori zona. Risponde con lo step da aprire, null senza zone
  openZones(): Observable<NewCustomerStep | null> {
    const zones$ = this.zones().length > 0
      ? of(this.zones())
      : this.http.get<NewCustomerOption[]>('api/ordini/nuovocliente/zone').pipe(
          tap(zones => {
            this.zones.set(zones);
            const oil = zones.filter(zone => zone.type === 'Oil');
            const nonOil = zones.filter(zone => zone.type === 'NonOil');
            this.zoneOil.set(oil.length === 1 ? oil[0] : null);
            this.zoneNonOil.set(nonOil.length === 1 ? nonOil[0] : null);
            this.territory.set(null);
          }),
        );

    this.loading.set(true);
    return zones$.pipe(
      // Senza esito (prima lettura o anagrafica cambiata) il territorio si controlla prima di scegliere lo step
      switchMap(zones => (zones.length > 0 && !this.territory() ? this.verifyTerritory() : of(null)).pipe(map(() => zones))),
      map(zones => (zones.length === 0 ? null : this.needsZoneChoice() || this.outOfZone() ? 'zones' : 'contact')),
      finalize(() => this.loading.set(false)),
    );
  }

  // Zone scelte e controllo del territorio, che non blocca: senza esito non c'è l'avviso
  setZones(oil: NewCustomerOption | null, nonOil: NewCustomerOption | null): void {
    this.zoneOil.set(oil);
    this.zoneNonOil.set(nonOil);
    this.verifyTerritory().subscribe();
  }

  setContact(contact: NewCustomerContactRequest): void {
    this.contact.set(contact);
  }

  // Condizioni di pagamento proponibili, lette una volta
  loadPayments(): Observable<NewCustomerOption[]> {
    if (this.payments().length > 0) {
      return of(this.payments());
    }

    this.loading.set(true);
    return this.http.get<NewCustomerOption[]>('api/ordini/nuovocliente/pagamenti').pipe(
      tap(payments => this.payments.set(payments)),
      finalize(() => this.loading.set(false)),
    );
  }

  setPayment(payment: NewCustomerPayment): void {
    this.payment.set(payment);
  }

  setNote(note: NewCustomerNoteRequest | null): void {
    this.note.set(note);
  }

  // Template GDPR per la natura giuridica del cliente, precompilato
  downloadGdprTemplate(): Observable<{ blob: Blob; fileName: string }> {
    return this.downloadTemplate('api/ordini/nuovocliente/templategdpr', 'consenso-gdpr.pdf');
  }

  // Scheda anagrafica con i dati bancari, precompilata, da far completare e firmare al cliente
  downloadBankTemplate(): Observable<{ blob: Blob; fileName: string }> {
    return this.downloadTemplate('api/ordini/nuovocliente/templatedatibancari', 'dati-bancari.pdf');
  }

  // Richiesta del nuovo cliente sulla proposta: dati in json e documenti come file. Il consenso GDPR si può caricare dopo
  create(skipGdpr: boolean): Observable<OrderProposal> {
    const registry = this.registry();
    const delivery = this.delivery();
    const contact = this.contact();
    const payment = this.payment();
    const note = this.note();
    if (!registry || !contact || !payment.condizione) {
      return throwError(() => new Error('Dati del nuovo cliente incompleti'));
    }

    const dati = {
      anagrafica: registry,
      destino: delivery ? {
        ...delivery,
        riferimento: delivery.riferimento?.trim() || null,
        nota: { ...delivery.nota, sponda: delivery.nota.sponda ?? false },
      } : {},
      idZonaOil: this.zoneOil()?.value ?? null,
      idZonaNonOil: this.zoneNonOil()?.value ?? null,
      contatto: contact,
      pagamento: {
        idCodicePagamento: payment.condizione.value,
        abi: payment.abi,
        cab: payment.cab,
        contatto: payment.condizione.type === 'CONTATTO' ? payment.contatto : null,
        codiceSdi: payment.codiceSdi || null,
        esenzioneIva: payment.esenzioneIva,
      },
      nota: note?.titolo ? note : null,
    };

    const formData = new FormData();
    formData.append('dati', JSON.stringify(dati));
    for (const file of this.bankDocuments()) {
      formData.append('moduloDatiBancari', file, file.name);
    }
    if (payment.esenzioneIva) {
      for (const file of this.vatDocuments()) {
        formData.append('esenzioneIva', file, file.name);
      }
    }
    if (!skipGdpr) {
      for (const file of this.gdprDocuments()) {
        formData.append('consensoGdpr', file, file.name);
      }
    }

    this.creating.set(true);
    return this.proposalService.setNewCustomer(formData).pipe(
      finalize(() => this.creating.set(false)),
    );
  }

  // Messaggio per l'utente: il 400 lo mostra già l'interceptor col motivo del backend
  errorMessage(error: unknown): string | null {
    return error instanceof HttpErrorResponse && error.status === 400 ? null : 'Il servizio al momento non risponde. Riprova più tardi.';
  }

  private verifyTerritory(): Observable<NewCustomerTerritory | null> {
    const oil = this.zoneOil();
    const nonOil = this.zoneNonOil();
    const registry = this.registry();
    this.territory.set(null);

    if (!registry || (!oil && !nonOil)) {
      return of(null);
    }

    return this.http.post<NewCustomerTerritory>('api/ordini/nuovocliente/verificaterritoriale', {
      anagrafica: registry,
      idZonaOil: oil?.value ?? null,
      idZonaNonOil: nonOil?.value ?? null,
    }).pipe(
      catchError(() => of(null)),
      tap(territory => {
        // Conta solo l'esito delle zone ancora scelte
        if (territory && this.zoneOil() === oil && this.zoneNonOil() === nonOil) {
          this.territory.set(territory);
        }
      }),
    );
  }

  private downloadTemplate(url: string, fallbackFileName: string): Observable<{ blob: Blob; fileName: string }> {
    const registry = this.registry();
    if (!registry) {
      return throwError(() => new Error('Anagrafica del nuovo cliente mancante'));
    }

    this.downloading.set(true);
    return this.http.post(url, registry, { observe: 'response', responseType: 'blob' }).pipe(
      map(response => ({
        blob: response.body as Blob,
        fileName: /filename=["']?([^;"']+)/i.exec(response.headers.get('content-disposition') ?? '')?.[1] ?? fallbackFileName,
      })),
      finalize(() => this.downloading.set(false)),
    );
  }

  private registryFromDetail(detail: NewCustomerCompanyDetail): NewCustomerRegistry {
    return {
      origine: 'Cribis',
      companyId: detail.companyId,
      ragioneSociale: detail.ragioneSociale,
      codiceFiscale: detail.codiceFiscale,
      partitaIva: detail.partitaIva,
      formaGiuridica: detail.formaGiuridica,
      codiceIstat: detail.codiceIstat,
      indirizzo: detail.indirizzo,
      cap: detail.cap,
      comune: detail.comune,
      provincia: detail.provincia,
      nazione: detail.nazione,
      pec: detail.pec,
      telefono: detail.telefono,
      latitudine: detail.latitudine,
      longitudine: detail.longitudine,
    };
  }
}
